import { useAuth0 } from "@auth0/auth0-react";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useAPI from "api/hooks/useAPI";
import useColors from "api/hooks/useColors";
import PrimaryButton from "components/buttons/PrimaryButton";
import Datepicker from "components/Datepicker";
import SpinningIcon from "components/SpinningIcon";
import AppPage from "components/structure/AppPage";
import { Toast } from "components/Toast";
import dayjs from "dayjs";
import { useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useInternalDashboard } from "./hooks/useInternalDashboard";
import InternalDashboardKpis from "./InternalDashboardKpis";
import InternalDashboardTrends from "./InternalDashboardTrends";
import InternalDashboardWidgetSelector from "./InternalDashboardWidgetSelector";
import AssignmentsUsageWidget from "./widgets/AssignmentsUsageWidget";
import ControlsTableWidget from "./widgets/ControlsTableWidget";
import CustomersTableWidget from "./widgets/CustomersTableWidget";
import EntitiesByCustomerWidget from "./widgets/EntitiesByCustomerWidget";
import EntitiesByEnvironmentTableWidget from "./widgets/EntitiesByEnvironmentTableWidget";
import EntitiesByEnvironmentWidget from "./widgets/EntitiesByEnvironmentWidget";
import EntitiesByTypeTableWidget from "./widgets/EntitiesByTypeTableWidget";
import IntegrationsByCustomerWidget from "./widgets/IntegrationsByCustomerWidget";
import NonApplicableControlsWidget from "./widgets/NonApplicableControlsWidget";
import ScansTableWidget from "./widgets/ScansTableWidget";
import SkippedAnalyzersTableWidget from "./widgets/SkippedAnalyzersTableWidget";
import SkippedDetectorsTableWidget from "./widgets/SkippedDetectorsTableWidget";
import TagsUsageWidget from "./widgets/TagsUsageWidget";
import ToolsTableWidget from "./widgets/ToolsTableWidget";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const InternalDashboard = () => {
  const { user } = useAuth0();
  const colors = useColors();
  const { getInternalDashboard } = useAPI();
  const {
    mounted,
    widgets,
    generateLayouts,
    onLayoutChange,
    onToggleWidget,
    gridConfig,
  } = useInternalDashboard();
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      "internalDashboard",
      selectedDate ? selectedDate.format("YYYY-MM-DD") : null,
    ],
    queryFn: async () => {
      const result = await getInternalDashboard(
        selectedDate ? selectedDate.format("YYYY-MM-DD") : null,
        null
      );
      return result.data;
    },
    enabled: !!user?.email?.includes("@breeze"),
    onError: (error) => {
      Toast.error("Failed to load internal dashboard data");
    },
  });

  const handleRefresh = () => {
    refetch();
  };

  if (!user?.email?.includes("@breeze")) {
    return <Box />;
  }

  return (
    <AppPage title="Internal Dashboard">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ minWidth: "220px", maxWidth: "240px" }}>
          <Datepicker
            value={selectedDate}
            onChange={setSelectedDate}
            label="Snapshot Date"
            disableFuture={true}
          />
        </Box>
        <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <PrimaryButton
            onClick={handleRefresh}
            disabled={isLoading}
            startIcon={<SpinningIcon isLoading={isLoading} />}
            size="small"
          >
            {isLoading ? "..." : "Refresh"}
          </PrimaryButton>
          <InternalDashboardWidgetSelector
            widgets={widgets}
            onToggleWidget={onToggleWidget}
          />
        </Box>
      </Box>

      <Box sx={{ padding: "0" }}>
        <ResponsiveReactGridLayout
          className="layout"
          layouts={generateLayouts}
          breakpoints={gridConfig.breakpoints}
          cols={gridConfig.cols}
          rowHeight={gridConfig.rowHeight}
          onLayoutChange={onLayoutChange}
          isDraggable={true}
          isResizable={false}
          measureBeforeMount={false}
          useCSSTransforms={mounted}
          style={{ background: colors.grey_100, borderRadius: "12px" }}
          compactType="vertical"
          preventCollision={false}
          margin={gridConfig.margin}
          containerPadding={gridConfig.containerPadding}
          draggableHandle={gridConfig.draggableHandle}
        >
          {widgets
            .filter((widget) => widget.isVisible)
            .map((widget) => (
              <div key={widget.id}>
                <InternalDashboardWidget
                  widget={widget}
                  colors={colors}
                  data={data}
                  isLoading={isLoading}
                />
              </div>
            ))}
        </ResponsiveReactGridLayout>
      </Box>
    </AppPage>
  );
};

const InternalDashboardWidget = ({ widget, colors, data, isLoading }) => {
  return (
    <Box
      sx={{
        backgroundColor: colors.common_white,
        boxShadow: `0px 0px 2px 1px ${colors.grey_24percent}`,
        borderRadius: "12px",
        overflow: "hidden",
        height: "100%",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        className="widget-drag-handle"
        sx={{
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
          marginBottom: "8px",
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "14px" }}>
          {widget.title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <InternalDashboardWidgetContent
          widget={widget}
          data={data}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

const InternalDashboardWidgetContent = ({ widget, data, isLoading }) => {
  switch (widget.componentType) {
    case "KPIs":
      return <InternalDashboardKpis summary={data?.summary} />;
    case "Trends":
      return <InternalDashboardTrends envId={null} trendPreset="7" />;
    case "CustomersTable":
      return (
        <CustomersTableWidget
          data={data}
          controls={data?.controls || []}
          isLoading={isLoading}
        />
      );
    case "ToolsTable":
      return (
        <ToolsTableWidget
          integrations={data?.integrations || []}
          isLoading={isLoading}
        />
      );
    case "ScansTable":
      const allScans = [
        ...(data?.in_progress_scans || []),
        ...(data?.failed_scans || []),
        ...(data?.successful_scans || []),
      ];
      return <ScansTableWidget scans={allScans} isLoading={isLoading} />;
    case "ControlsTable":
      return (
        <ControlsTableWidget
          controls={data?.controls || []}
          isLoading={isLoading}
        />
      );
    case "EntitiesByTypeTable":
      return (
        <EntitiesByTypeTableWidget
          summary={data?.summary}
          isLoading={isLoading}
        />
      );
    case "EntitiesByEnvironmentTable":
      return (
        <EntitiesByEnvironmentTableWidget
          summary={data?.summary}
          isLoading={isLoading}
        />
      );
    case "IntegrationsByCustomer":
      return (
        <IntegrationsByCustomerWidget
          customers={data?.customers || []}
          isLoading={isLoading}
        />
      );
    case "EntitiesByEnvironment":
      return (
        <EntitiesByEnvironmentWidget
          summary={data?.summary}
          isLoading={isLoading}
        />
      );
    case "EntitiesByCustomer":
      return (
        <EntitiesByCustomerWidget
          customers={data?.customers || []}
          isLoading={isLoading}
        />
      );
    case "SkippedAnalyzersTable":
      return (
        <SkippedAnalyzersTableWidget
          skippedAnalyzers={data?.skipped_analyzers || []}
          isLoading={isLoading}
        />
      );
    case "SkippedDetectorsTable":
      return (
        <SkippedDetectorsTableWidget
          skippedDetectors={data?.skipped_detectors || []}
          isLoading={isLoading}
        />
      );
    case "NonApplicableControls":
      return (
        <NonApplicableControlsWidget
          nonApplicableControls={data?.non_applicable_controls || []}
          isLoading={isLoading}
        />
      );
    case "TagsUsage":
      return (
        <TagsUsageWidget
          customers={data?.customers || []}
          isLoading={isLoading}
        />
      );
    case "AssignmentsUsage":
      return (
        <AssignmentsUsageWidget
          customers={data?.customers || []}
          isLoading={isLoading}
        />
      );
    default:
      return null;
  }
};

export default InternalDashboard;
